import { DEFAULT_VARIANT_TITLE } from "@smartrr/shared/constants";
import {
  getSmartrrVariantDiscountPrice,
  getVariantPriceForCurrency,
} from "@smartrr/shared/entities/PurchasableVariant";
import { CurrencyCode } from "@smartrr/shared/shopifyGraphQL/api";
import { formatMoney } from "@smartrr/shared/utils/formatMoney";
import { SurfaceableError } from "@smartrr/shared/utils/SurfaceableError";

import { VariantPurchaseOptions } from "@customer-app/state/reducers/sellingPlanGroups";

interface VariantOption {
  value: string;
  label: string;
}

export function createVariantSelectorOptions(
  variantOptions: VariantPurchaseOptions[],
  currency: CurrencyCode,
  includeAddOns = false,
  isAddOnSelected = false,
  modernFormat = false,
  includeTrending = false,
  isTrendingSelected = false
): VariantOption[] {
  return variantOptions
    .filter(vntOption => vntOption.vnt.purchasableVariantName !== DEFAULT_VARIANT_TITLE)
    .filter(vntOption => {
      if (includeTrending) {
        return !!vntOption.trendingConfig;
      } else if (includeAddOns) {
        return !!vntOption.addonConfig;
      }
      return !!vntOption.sellingPlan;
    })
    .map(vntOption => {
      if (
        !vntOption.vnt.presentmentPrices.find(presentmentPrice => presentmentPrice.priceCurrency === currency)
          ?.price
      ) {
        if (
          vntOption.vnt.presentmentPrices.find(
            presentmentPrice => presentmentPrice.priceCurrency === CurrencyCode.Usd
          )?.price
        ) {
          currency = CurrencyCode.Usd;
        } else {
          currency = vntOption.vnt.presentmentPrices[0].priceCurrency as CurrencyCode;
        }
      }
      const variantPriceForCurrency = getVariantPriceForCurrency(vntOption.vnt, currency);

      const sellingPlan = (
        isAddOnSelected
          ? [vntOption.addonConfig, vntOption.sellingPlan]
          : isTrendingSelected
          ? [vntOption.trendingConfig, vntOption.sellingPlan]
          : [vntOption.sellingPlan, vntOption.addonConfig]
      ).find(Boolean)!;

      if (!variantPriceForCurrency) {
        throw new SurfaceableError(
          `No price for variant ${vntOption.vnt.id} for selling plan ${sellingPlan.shopifyNumericId} currency of ${currency}`
        );
      }

      const price = getSmartrrVariantDiscountPrice(vntOption.vnt, currency, sellingPlan);
      if (modernFormat) {
        return {
          value: `${vntOption.vnt.id}`,
          label: `${vntOption.vnt.purchasableVariantName}`,
        };
      }
      return {
        value: `${vntOption.vnt.id}`,
        label: `${vntOption.vnt.purchasableVariantName} (${formatMoney(price, currency)})`,
      };
    });
}
